import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface Airport {
  id: number;
  code: string;
  name: string;
  displayName: string;
  isActive: boolean;
  createdAt: string | null;
}

export interface CreateAirportRequest {
  code: string;
  name: string;
  displayName: string;
  isActive?: boolean;
}

export interface UpdateAirportRequest {
  code?: string;
  name?: string;
  displayName?: string;
  isActive?: boolean;
}

export interface AirportSearchRequest {
  code?: string;
  name?: string;
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class AirportService {
  private baseUrl = '/api/airports';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех аэропортов с пагинацией
  async getAirports(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<Airport>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение активных аэропортов
  async getActiveAirports(): Promise<Airport[]> {
    const url = getDataApiUrl(`${this.baseUrl}/active`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение аэропорта по ID
  async getAirportById(id: number): Promise<Airport> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Создание нового аэропорта
  async createAirport(data: CreateAirportRequest): Promise<Airport> {
    const url = getDataApiUrl(this.baseUrl);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Обновление аэропорта
  async updateAirport(id: number, data: UpdateAirportRequest): Promise<Airport> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Удаление аэропорта
  async deleteAirport(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
  }

  // Активация/деактивация аэропорта
  async toggleAirport(id: number): Promise<Airport> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}/toggle`);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Поиск аэропортов
  async searchAirports(searchRequest: AirportSearchRequest, page = 0, size = 20): Promise<PaginatedResponse<Airport>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...Object.fromEntries(
        Object.entries(searchRequest).filter(([_, value]) => value !== undefined && value !== '')
      )
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение статистики аэропортов
  async getAirportStats(): Promise<{
    totalAirports: number;
    activeAirports: number;
    inactiveAirports: number;
  }> {
    const url = getDataApiUrl(`${this.baseUrl}/stats`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }
}

const airportService = new AirportService();
export default airportService;
